import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Gauge } from "@mui/x-charts/Gauge";
import { PieChart } from "@mui/x-charts/PieChart";
import PropTypes from "prop-types";

export default function SLAComplianceWidget(props) {
  const theme = useTheme();
  const data = props.data || {};

  // SLA data structure
  const complianceRate = data.complianceRate || 0;
  const targetRate = data.targetRate || 95;
  const changePercent = data.changePercent || 0;
  const breakdown = data.breakdown || {};

  // Total tickets for calculations
  const totalTickets =
    (breakdown.onTime || 0) +
    (breakdown.nearBreach || 0) +
    (breakdown.breached || 0);

  // Color coding for SLA performance
  const getPerformanceColor = (rate) => {
    if (rate >= targetRate) return theme.palette.success.main;
    if (rate >= targetRate - 10) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Performance status
  const getPerformanceStatus = (rate) => {
    if (rate >= targetRate) return "Excellent";
    if (rate >= targetRate - 10) return "Good";
    return "Needs Attention";
  };

  // Performance icon
  const getPerformanceIcon = (rate) => {
    if (rate >= targetRate)
      return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
    if (rate >= targetRate - 10)
      return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
    return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
  };

  // Data for donut chart
  const donutData = [
    {
      id: "onTime",
      label: "On Time",
      value: breakdown.onTime || 0,
      color: theme.palette.success.main,
    },
    {
      id: "nearBreach",
      label: "Near Breach",
      value: breakdown.nearBreach || 0,
      color: theme.palette.warning.main,
    },
    {
      id: "breached",
      label: "Breached",
      value: breakdown.breached || 0,
      color: theme.palette.error.main,
    },
  ];

  return (
    <>
      <Stack sx={{ justifyContent: "space-between", mb: 2 }}>
        <Stack
          direction="row"
          sx={{
            alignContent: { xs: "center", sm: "flex-start" },
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="h4"
            component="p"
            sx={{ color: getPerformanceColor(complianceRate) }}
          >
            {complianceRate.toFixed(1)}%
          </Typography>
          {getPerformanceIcon(complianceRate)}
          <Chip
            size="small"
            color={changePercent > 0 ? "success" : "error"}
            label={`${changePercent > 0 ? "+" : ""}${changePercent}%`}
          />
        </Stack>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {props.title || "SLA Compliance Rate"}
        </Typography>
      </Stack>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* SLA Gauge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 140,
          }}
        >
          <Gauge
            value={complianceRate}
            valueMin={0}
            valueMax={100}
            startAngle={-110}
            endAngle={110}
            sx={{
              [`& .MuiGauge-valueText`]: {
                fontSize: 18,
                transform: "translate(0px, 0px)",
                fill: getPerformanceColor(complianceRate),
                fontWeight: 600,
              },
              [`& .MuiGauge-valueArc`]: {
                fill: getPerformanceColor(complianceRate),
              },
              [`& .MuiGauge-referenceArc`]: {
                fill: theme.palette.divider,
              },
            }}
            text={({ value }) => `${value.toFixed(1)}%`}
          />
        </Box>

        {/* Target vs Actual */}
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 2 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: "text.primary" }}
            >
              {complianceRate.toFixed(1)}%
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Current
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: theme.palette.primary.main }}
            >
              {targetRate}%
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Target
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{
                color:
                  complianceRate >= targetRate
                    ? theme.palette.success.main
                    : theme.palette.error.main,
              }}
            >
              {complianceRate >= targetRate ? "+" : ""}
              {(complianceRate - targetRate).toFixed(1)}%
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              vs Target
            </Typography>
          </Box>
        </Box>

        {/* Performance Status */}
        <Box
          sx={{
            textAlign: "center",
            py: 1,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.02)",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Status: {getPerformanceStatus(complianceRate)}
          </Typography>
        </Box>

        {/* Donut Chart for SLA Breakdown */}
        <Box sx={{ height: 180, mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mb: 1,
              display: "block",
              textAlign: "center",
            }}
          >
            SLA Performance Breakdown
          </Typography>
          <PieChart
            series={[
              {
                data: donutData,
                innerRadius: 40,
                outerRadius: 70,
                paddingAngle: 2,
                cornerRadius: 4,
                highlightScope: { faded: "global", highlighted: "item" },
              },
            ]}
            height={150}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
                itemMarkWidth: 12,
                itemMarkHeight: 12,
                markGap: 8,
                itemGap: 16,
                labelStyle: {
                  fontSize: 12,
                  fill: theme.palette.text.secondary,
                },
              },
            }}
          />
        </Box>

        {/* Summary Stats */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            pt: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: theme.palette.success.main }}
            >
              {breakdown.onTime || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              On Time
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: theme.palette.warning.main }}
            >
              {breakdown.nearBreach || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Near Breach
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: theme.palette.error.main }}
            >
              {breakdown.breached || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Breached
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              component="p"
              sx={{ color: "text.primary" }}
            >
              {totalTickets}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Total
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}

SLAComplianceWidget.propTypes = {
  data: PropTypes.object,
  title: PropTypes.string,
};
